import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";

const MEDIA = process.env.REACT_APP_BACKEND_URL + "/api/files/noirbyamara/teaser";

const pad = (n) => String(n).padStart(2, "0");

const Unit = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <span className="font-serif text-4xl sm:text-6xl text-cream tabular-nums leading-none [text-shadow:0_2px_22px_rgba(0,0,0,0.65)]">{pad(value)}</span>
    <span className="mt-2 text-[0.6rem] sm:text-xs tracking-[0.28em] uppercase text-gold [text-shadow:0_1px_10px_rgba(0,0,0,0.6)]">{label}</span>
  </div>
);

const ComingSoon = () => {
  const { settings } = useStore();
  const s = settings || {};
  const target = s.launch_at ? new Date(s.launch_at).getTime() : null;
  const [left, setLeft] = useState(target ? target - Date.now() : 0);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [claimed, setClaimed] = useState(null);
  useEffect(() => { api.get("/launch/count").then((r) => setClaimed(r.data)).catch(() => {}); }, []);
  const videoRef = useRef(null);
  const readOff = () => { try { return JSON.parse(localStorage.getItem("nba_sound_off")) === true; } catch { return false; } };
  const [soundOn, setSoundOn] = useState(false);

  // The countdown's teaser video is the ONLY audio here — stop the global
  // storefront playlist from auto-starting on top of it (was playing two songs).
  useEffect(() => {
    window.__nbaSuppressSound = true;
    return () => { window.__nbaSuppressSound = false; };
  }, []);

  // Start audio on the visitor's first interaction (browsers block sound on load),
  // unless they previously chose silence (shared preference with the live store).
  useEffect(() => {
    const start = () => {
      const v = videoRef.current;
      if (v && !readOff()) {
        v.muted = false;
        v.volume = 0.7;
        v.play().catch(() => {});
        setSoundOn(true);
      }
      remove();
    };
    const remove = () => {
      ["scroll", "wheel", "pointerdown", "touchstart", "keydown", "click"].forEach((e) =>
        window.removeEventListener(e, start));
    };
    ["scroll", "wheel", "pointerdown", "touchstart", "keydown", "click"].forEach((e) =>
      window.addEventListener(e, start, { once: false, passive: true }));
    return remove;
  }, []);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !soundOn;
    v.muted = !next;
    if (next) { v.volume = 0.7; v.play().catch(() => {}); }
    setSoundOn(next);
    // Remember the choice for the live store too (shared preference).
    try { localStorage.setItem("nba_sound_off", JSON.stringify(!next)); } catch {}
  };

  useEffect(() => {
    if (!target) return;
    const tick = () => setLeft(target - Date.now());
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [target]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/launch/subscribe", { email: email.trim() });
      setDone(true);
      toast.success("You're on the list. We'll write the moment we open.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Please enter a valid email.");
    }
    setBusy(false);
  };

  const d = Math.max(0, Math.floor(left / 86400000));
  const h = Math.max(0, Math.floor(left / 3600000) % 24);
  const m = Math.max(0, Math.floor(left / 60000) % 60);
  const sec = Math.max(0, Math.floor(left / 1000) % 60);
  const launched = !!(target && left <= 0);

  return (
    <div data-testid="coming-soon" className="min-h-screen bg-cocoa text-cream flex flex-col items-center justify-center px-6 py-16 relative overflow-hidden">
      <video
        ref={videoRef}
        autoPlay muted loop playsInline
        poster={`${MEDIA}/ori_teaser_poster.jpg`}
        data-testid="coming-soon-video"
        className="absolute inset-0 w-full h-full object-cover opacity-30">
        <source src={`${MEDIA}/ori_teaser.mp4`} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-b from-cocoa/85 via-cocoa/70 to-cocoa/95" />
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(ellipse 62% 58% at 50% 46%, rgba(26,14,10,0.72) 0%, rgba(26,14,10,0.32) 46%, transparent 76%)" }} />

      <button
        onClick={toggleSound}
        data-testid="coming-soon-sound-toggle"
        aria-label={soundOn ? "Mute music" : "Play music"}
        className="absolute top-6 right-6 z-20 flex items-center gap-2 border border-cream/25 rounded-full px-4 py-2 text-[0.6rem] tracking-[0.2em] uppercase text-cream/80 hover:border-gold hover:text-gold transition-colors backdrop-blur-sm bg-cocoa/30">
        {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
        {soundOn ? "Sound On" : "Sound Off"}
      </button>
      <div className="pointer-events-none absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-wine/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[36rem] h-[36rem] rounded-full bg-gold/10 blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-2xl text-center">

        <p className="overline text-gold mb-6 [text-shadow:0_1px_12px_rgba(0,0,0,0.6)]">{launched ? "Now Open" : "Opening Soon"}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-wide [text-shadow:0_2px_28px_rgba(0,0,0,0.6)]">
          NOIRBYAMARAORI
        </h1>
        <p className="mt-6 text-base sm:text-lg text-cream/80 leading-relaxed max-w-lg mx-auto [text-shadow:0_1px_16px_rgba(0,0,0,0.6)]">
          {s.launch_subheading || "A heritage-led house of contemporary pieces. We are preparing something considered, and worth the wait."}
        </p>

        {target && left > 0 && (
          <div data-testid="coming-soon-countdown" className="mt-12 flex items-center justify-center gap-5 sm:gap-9">
            <Unit value={d} label="Days" />
            <span className="font-serif text-3xl sm:text-5xl text-cream/30 -mt-4">:</span>
            <Unit value={h} label="Hours" />
            <span className="font-serif text-3xl sm:text-5xl text-cream/30 -mt-4">:</span>
            <Unit value={m} label="Minutes" />
            <span className="font-serif text-3xl sm:text-5xl text-cream/30 -mt-4">:</span>
            <Unit value={sec} label="Seconds" />
          </div>
        )}

        <div className="mt-14 max-w-md mx-auto">
          {launched ? (
            <a href="/" data-testid="coming-soon-enter"
              className="inline-block bg-gold text-cocoa px-9 py-3.5 text-[0.7rem] tracking-[0.2em] uppercase hover:bg-cream transition-colors">
              Shop Now — We're Live
            </a>
          ) : done ? (
            <p data-testid="coming-soon-success" className="font-serif text-xl text-gold">
              Thank you. You'll be the first to know.
            </p>
          ) : (
            <form onSubmit={submit} className="text-left">
              {claimed && (
                <p data-testid="first-100" className="text-center text-[0.7rem] tracking-[0.2em] uppercase text-gold mb-3">
                  <span className="text-cream">The First 100</span> · {claimed.count} claimed{claimed.remaining > 0 ? ` · ${claimed.remaining} places left` : " · waitlist open"}
                </p>
              )}
              <p className="text-center text-[0.7rem] tracking-[0.22em] uppercase text-cream/50 mb-4">
                Be first through the door
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  required
                  data-testid="coming-soon-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="flex-1 bg-transparent border border-cream/25 px-4 py-3 text-sm text-cream placeholder-cream/40 outline-none focus:border-gold transition-colors"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="coming-soon-submit"
                  className="bg-gold text-cocoa px-7 py-3 text-[0.68rem] tracking-[0.18em] uppercase hover:bg-cream transition-colors disabled:opacity-60">
                  {busy ? "…" : "Notify Me"}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-16 text-[0.62rem] tracking-[0.18em] uppercase text-cream/40">
          Glasgow · United Kingdom
        </p>
      </motion.div>
    </div>
  );
};

export default ComingSoon;
